import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback
} from 'react';
import {
  Button,
  Form,
  Input,
  Select,
  DatePicker,
  Tabs,
  Tag,
  Space,
  Tooltip,
  Table,
  Spin,
  Empty,
  Row,
  Col,
  Card,
  Statistic,
  Alert
} from 'antd';
import {
  DownloadOutlined,
  EyeOutlined,
  FileTextOutlined,
  StopOutlined,
  CheckCircleOutlined,
  ScanOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Layout from '@/common/Layout';
import CustomTable from '@/common/Content/CustomTable';
import CustomModal from '@/common/CustomModal';
import {
  getSieveLabels,
  getSieveCounts,
  getSieveLogs,
  bulkCleanSieves,
  bulkAllocateSieves,
  bulkStatusChangeSieves,
  generateSieveUsageLogReport,
  bulkInactivateSieves,
  reactivateSieve
} from '@/features/sieveLabel/redux/slice';
import useSieveAccess from '@/features/sieveLabel/hooks/useSieveAccess';
import { getProcesses } from '@/features/productionLabelGen/redux/slice';
import {
  buildBlobPdfUrl,
  getStatusFromRecord,
  STATUS_CONFIG,
  ACTION_LABELS,
  getColumnSearchProps
} from './SieveAllocation.helper';
import { get_sieve_labels } from '@/features/sieveLabel/redux/api';
import SieveScanModal from './SieveScanModal';
import BulkSieveScanFilterModal from './BulkSieveScanFilterModal';
import styles from './index.module.less';

const { TabPane } = Tabs;

const prefetchCacheKey = (p, pp, tab, search) => `${p}-${pp}-${tab}-${search}`;

const SieveLabelList = ({ labels }) => {
  const visible = labels.slice(0, 10);
  const overflow = labels.length - visible.length;
  return (
    <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {visible.map((l) => (
        <Tag key={l} style={{ fontFamily: 'monospace' }}>
          {l}
        </Tag>
      ))}
      {overflow > 0 && <Tag color="default">+{overflow} more</Tag>}
    </div>
  );
};

const SieveAllocation = () => {
  const dispatch = useDispatch();
  const {
    list,
    listPagination,
    loading,
    counts,
    sieveLogs,
    sieveLogsLoading,
    reportGenerating
  } = useSelector((state) => state.sieveLabel);

  // Pagination & filter state
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(
    () => Number(localStorage.getItem('sieve_perPage')) || 10
  );
  const [activeTab, setActiveTab] = useState(
    () => localStorage.getItem('sieve_activeTab') || 'all'
  );
  const [batchSearch, setBatchSearch] = useState('');
  const [seededList, setSeededList] = useState(null);
  const prefetchRef = useRef(null);

  // Cross-page selection: store full item info so status is available across pages
  const [selectedItems, setSelectedItems] = useState([]);
  const selectedIds = selectedItems.map((i) => i.id);

  // Modal state
  const [modalType, setModalType] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [logSieveLabel, setLogSieveLabel] = useState(null);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [scanFilter, setScanFilter] = useState(null); // null = inactive, string[] = active
  const [bulkScanModalOpen, setBulkScanModalOpen] = useState(false);

  // Forms
  const [bulkAllocForm] = Form.useForm();
  const [reportForm] = Form.useForm();
  const [inactivateForm] = Form.useForm();
  const [reactivateForm] = Form.useForm();
  const [reactivateSieveLabel, setReactivateSieveLabel] = useState(null);
  const [bulkAllocStep, setBulkAllocStep] = useState('form'); // 'form' | 'scan'
  const [bulkAllocFormValues, setBulkAllocFormValues] = useState(null);
  const { canAct, canReactivate } = useSieveAccess();

  // Row highlight after actions
  const [highlightedIds, setHighlightedIds] = useState(new Set());
  const flashTimerRef = useRef(null);
  const flashRows = useCallback((labelNumbers) => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    setHighlightedIds(new Set(labelNumbers));
    flashTimerRef.current = setTimeout(
      () => setHighlightedIds(new Set()),
      2000
    );
  }, []);

  // Product master options
  const [processOptions, setProcessOptions] = useState([]);
  const [nextStageOptions, setNextStageOptions] = useState([]);

  // Fetch list whenever page / perPage / tab / batchSearch / scanFilter changes
  useEffect(() => {
    fetchList();
  }, [page, perPage, activeTab, batchSearch, scanFilter]);

  // Fetch counts on mount
  useEffect(() => {
    dispatch(getSieveCounts());
  }, []);

  // Persist perPage preference
  useEffect(() => {
    localStorage.setItem('sieve_perPage', perPage);
  }, [perPage]);

  // Prefetch next page silently after current page finishes loading
  useEffect(() => {
    if (loading) return;
    const totalPages = Math.ceil(
      (listPagination?.total_records || 0) / perPage
    );
    if (page >= totalPages) return;
    const nextPage = page + 1;
    const cacheKey = prefetchCacheKey(
      nextPage,
      perPage,
      activeTab,
      batchSearch
    );
    if (prefetchRef.current?.cacheKey === cacheKey) return;
    prefetchRef.current = null;
    const payload = { page: nextPage, per_page: perPage };
    if (activeTab !== 'all') payload.tab = activeTab;
    if (batchSearch) payload.batch_search = batchSearch;
    if (scanFilter && scanFilter.length > 0)
      payload.sieve_labels = scanFilter.join(',');
    get_sieve_labels(payload).then((res) => {
      if (res?.success)
        prefetchRef.current = { cacheKey, data: res.data || [] };
    });
  }, [loading, page, perPage, activeTab, batchSearch, scanFilter]);

  // Clear seeded list once real data arrives
  useEffect(() => {
    if (!loading && seededList) setSeededList(null);
  }, [loading]);

  // Load product master dropdowns once
  useEffect(() => {
    dispatch(getProcesses({ type: 'process' })).then((res) => {
      if (res?.payload?.success) setProcessOptions(res.payload.data || []);
    });
    dispatch(getProcesses({ type: 'nextStage' })).then((res) => {
      if (res?.payload?.success) setNextStageOptions(res.payload.data || []);
    });
  }, []);

  const fetchList = () => {
    const payload = { page, per_page: perPage };
    if (activeTab !== 'all') payload.tab = activeTab;
    if (batchSearch) payload.batch_search = batchSearch;
    if (scanFilter && scanFilter.length > 0)
      payload.sieve_labels = scanFilter.join(',');
    dispatch(getSieveLabels(payload));
  };

  const refreshAll = () => {
    fetchList();
    dispatch(getSieveCounts());
  };

  // Bulk button logic
  const selectionStatus = useMemo(() => {
    if (!selectedItems.length) return null;
    const statuses = [...new Set(selectedItems.map((i) => i.status))];
    if (statuses.length > 1) return 'mixed';
    return statuses[0];
  }, [selectedItems]);

  const canMarkCleaned = selectionStatus === 'to_be_cleaned';
  const canAssignBatch = selectionStatus === 'cleaned';
  const canSendForCleaning = selectionStatus === 'in_use';
  const isMixed = selectionStatus === 'mixed';

  const canInactivate = useMemo(() => {
    if (!selectedItems.length) return false;
    return selectedItems.every(
      (i) => i.status === 'to_be_cleaned' || i.status === 'cleaned'
    );
  }, [selectedItems]);

  // Row selection (cross-page aware)
  const rowSelection = {
    selectedRowKeys: selectedIds,
    onChange: (keys, rows) => {
      const currentPageIds = (seededList || list).map((r) => r.id);
      const otherPageItems = selectedItems.filter(
        (item) => !currentPageIds.includes(item.id)
      );
      const currentPageSelected = rows.map((r) => ({
        id: r.id,
        status: getStatusFromRecord(r),
        sieve_label_number: r.sieve_label_number
      }));
      setSelectedItems([...otherPageItems, ...currentPageSelected]);
    },
    getCheckboxProps: (record) => ({
      disabled: !record.is_active || record.is_retired
    })
  };

  const clearSelection = () => setSelectedItems([]);

  // Tab change
  const handleTabChange = (key) => {
    localStorage.setItem('sieve_activeTab', key);
    prefetchRef.current = null;
    setActiveTab(key);
    setPage(1);
    clearSelection();
    setScanFilter(null);
    setFilteredInfo({});
  };

  // Card click
  const handleCardClick = (tab) => {
    localStorage.setItem('sieve_activeTab', tab);
    prefetchRef.current = null;
    setActiveTab(tab);
    setPage(1);
    setScanFilter(null);
    clearSelection();
  };

  // Page/size change — seed from prefetch if available
  const handlePageChange = (p, ps) => {
    if (ps !== perPage) {
      prefetchRef.current = null;
    } else {
      const cacheKey = prefetchCacheKey(p, perPage, activeTab, batchSearch);
      if (prefetchRef.current?.cacheKey === cacheKey) {
        setSeededList(prefetchRef.current.data);
        prefetchRef.current = null;
      }
    }
    setPage(p);
    setPerPage(ps);
  };

  // Modal helpers
  const closeModal = () => {
    if (submitting) return;
    setModalType(null);
  };

  const hasActiveFilters =
    Object.values(filteredInfo).some((v) => v && v.length > 0) ||
    !!batchSearch ||
    !!scanFilter ||
    selectedItems.length > 0;

  const handleClearFilters = () => {
    setFilteredInfo({});
    setBatchSearch('');
    setScanFilter(null);
    clearSelection();
    setPage(1);
    prefetchRef.current = null;
  };

  // Bulk: Mark Cleaned — with QR scan verification
  const handleBulkCleanWithScan = async (confirmedLabels) => {
    const confirmedIds = selectedItems
      .filter((i) => confirmedLabels.includes(i.sieve_label_number))
      .map((i) => i.id);
    if (!confirmedIds.length) return;
    setSubmitting(true);
    const res = await dispatch(bulkCleanSieves({ ids: confirmedIds }));
    setSubmitting(false);
    if (res?.payload?.success) {
      flashRows(confirmedLabels);
      clearSelection();
      refreshAll();
      closeModal();
    }
  };

  // Bulk: Assign to Batch — step 1 form submission (captures values, advances to scan)
  const handleBulkAllocate = async (values) => {
    setBulkAllocFormValues(values);
    setBulkAllocStep('scan');
  };

  // Bulk: Assign to Batch — step 2 scan confirmation
  const handleBulkAllocateWithScan = async (confirmedLabels) => {
    const confirmedIds = selectedItems
      .filter((i) => confirmedLabels.includes(i.sieve_label_number))
      .map((i) => i.id);
    if (!confirmedIds.length) return;
    setSubmitting(true);
    const res = await dispatch(
      bulkAllocateSieves({ ids: confirmedIds, ...bulkAllocFormValues })
    );
    setSubmitting(false);
    if (res?.payload?.success) {
      flashRows(confirmedLabels);
      clearSelection();
      refreshAll();
      closeModal();
      setBulkAllocStep('form');
      setBulkAllocFormValues(null);
    }
  };

  // Bulk: Send for Cleaning
  const handleBulkStatusChange = async () => {
    setSubmitting(true);
    const res = await dispatch(bulkStatusChangeSieves({ ids: selectedIds }));
    setSubmitting(false);
    if (res?.payload?.success) {
      flashRows(selectedItems.map((i) => i.sieve_label_number));
      clearSelection();
      refreshAll();
      closeModal();
    }
  };

  const handleBulkInactivate = async (values) => {
    setSubmitting(true);
    const res = await dispatch(
      bulkInactivateSieves({ ids: selectedIds, reason: values.reason })
    );
    setSubmitting(false);
    if (res?.payload?.success) {
      flashRows(selectedItems.map((i) => i.sieve_label_number));
      clearSelection();
      refreshAll();
      closeModal();
      inactivateForm.resetFields();
    }
  };

  const handleReactivate = async (values) => {
    setSubmitting(true);
    const res = await dispatch(
      reactivateSieve({
        sieve_label_number: reactivateSieveLabel,
        note: values.note
      })
    );
    setSubmitting(false);
    if (res?.payload?.success) {
      flashRows([reactivateSieveLabel]);
      setReactivateSieveLabel(null);
      refreshAll();
      setModalType(null);
      reactivateForm.resetFields();
    }
  };

  // Per-sieve log modal
  const openLogModal = (record) => {
    setLogSieveLabel(record.sieve_label_number);
    dispatch(getSieveLogs(record.sieve_label_number));
    setModalType('sieve_log');
  };

  const handleDownloadSievePdf = () => {
    dispatch(
      generateSieveUsageLogReport({ sieve_label_number: logSieveLabel })
    );
  };

  // Usage log report
  const handleGenerateReport = async (values) => {
    const from_date = values.from_date.format('YYYY-MM-DD');
    const to_date = values.to_date.format('YYYY-MM-DD');
    await dispatch(generateSieveUsageLogReport({ from_date, to_date }));
    setModalType(null);
    reportForm.resetFields();
  };

  const emptyText = useMemo(() => {
    if (scanFilter)
      return `No sieve/screen items found for the scanned filter (${
        scanFilter.length
      } label${scanFilter.length !== 1 ? 's' : ''})`;
    if (batchSearch)
      return `No sieve/screen items match batch number "${batchSearch}"`;
    if (activeTab !== 'all') return 'No sieve/screen items in this status yet';
    return 'No Sieve/Screen labels have been created yet';
  }, [batchSearch, activeTab, scanFilter]);

  // Column definitions
  const columns = useMemo(
    () => [
      {
        title: 'Sieve/Screen Label',
        dataIndex: 'sieve_label_number',
        key: 'sieve_label_number',
        width: 160,
        defaultSortOrder: 'descend',
        sorter: (a, b) => {
          const na = parseInt(a.sieve_label_number.replace(/\D/g, ''), 10);
          const nb = parseInt(b.sieve_label_number.replace(/\D/g, ''), 10);
          return na - nb;
        },
        render: (v) => (
          <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{v}</span>
        ),
        filteredValue: filteredInfo['sieve_label_number'] || null,
        ...getColumnSearchProps('sieve_label_number')
      },
      {
        title: 'Type',
        dataIndex: 'item_type',
        key: 'item_type',
        width: 100,
        render: (v) => (
          <Tag color={v === 'Screen' ? 'purple' : 'blue'}>{v || 'Sieve'}</Tag>
        )
      },
      {
        title: 'In-house No.',
        dataIndex: 'inhouse_number',
        key: 'inhouse_number',
        width: 130,
        ellipsis: { showTitle: true },
        render: (v) => v || '—',
        filteredValue: filteredInfo['inhouse_number'] || null,
        ...getColumnSearchProps('inhouse_number')
      },
      {
        title: 'Status',
        key: 'status',
        width: 140,
        render: (_, record) => {
          const s = getStatusFromRecord(record);
          const cfg = STATUS_CONFIG[s];
          return <Tag color={cfg.color}>{cfg.label}</Tag>;
        }
      },
      {
        title: 'Product Batch No.',
        dataIndex: 'product_batch_number',
        key: 'product_batch_number',
        width: 160,
        ellipsis: { showTitle: true },
        sorter: (a, b) =>
          (a.product_batch_number || '').localeCompare(
            b.product_batch_number || ''
          ),
        render: (v) => v || '—',
        filteredValue: filteredInfo['product_batch_number'] || null,
        ...getColumnSearchProps('product_batch_number')
      },
      {
        title: 'Product Name',
        dataIndex: 'product_name',
        key: 'product_name',
        width: 180,
        ellipsis: { showTitle: true },
        sorter: (a, b) =>
          (a.product_name || '').localeCompare(b.product_name || ''),
        render: (v) => v || '—',
        filteredValue: filteredInfo['product_name'] || null,
        ...getColumnSearchProps('product_name')
      },
      {
        title: 'Process',
        dataIndex: 'process',
        key: 'process',
        width: 130,
        ellipsis: { showTitle: true },
        render: (v) => v || '—',
        filteredValue: filteredInfo['process'] || null,
        ...getColumnSearchProps('process')
      },
      {
        title: 'Next Stage',
        dataIndex: 'nextstage',
        key: 'nextstage',
        width: 130,
        ellipsis: { showTitle: true },
        render: (v) => v || '—',
        filteredValue: filteredInfo['nextstage'] || null,
        ...getColumnSearchProps('nextstage')
      },
      {
        title: 'Created',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 170,
        sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
        render: (v) =>
          v ? moment(v).utcOffset('+05:30').format('DD MMM YYYY HH:mm') : '—'
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 90,
        fixed: 'right',
        render: (_, record) => (
          <Space size={8}>
            <Tooltip
              title={record.pdf_url ? 'Download Label PDF' : 'PDF not ready'}
            >
              <Button
                type="link"
                size="small"
                icon={<DownloadOutlined />}
                href={
                  record.pdf_url ? buildBlobPdfUrl(record.pdf_url) : undefined
                }
                target="_blank"
                rel="noopener noreferrer"
                download={
                  record.pdf_url
                    ? `${record.sieve_label_number}.pdf`
                    : undefined
                }
                disabled={!record.pdf_url}
                style={{ padding: 0 }}
              />
            </Tooltip>
            <Tooltip title="View Activity Log">
              <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => openLogModal(record)}
                style={{ padding: 0 }}
              />
            </Tooltip>
            {record.is_retired && canReactivate && (
              <Tooltip title="Reactivate Sieve/Screen">
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0, color: '#52c41a' }}
                  onClick={() => {
                    setReactivateSieveLabel(record.sieve_label_number);
                    reactivateForm.resetFields();
                    setModalType('reactivate');
                  }}
                >
                  Reactivate
                </Button>
              </Tooltip>
            )}
          </Space>
        )
      }
    ],
    [filteredInfo, page, perPage, canReactivate]
  );

  const logColumns = [
    {
      title: 'Timestamp (IST)',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 190,
      render: (v) =>
        v
          ? moment(v).utcOffset('+05:30').format('DD MMM YYYY HH:mm:ss') +
            ' IST'
          : '—'
    },
    {
      title: 'Activity',
      dataIndex: 'action',
      key: 'action',
      width: 190,
      render: (v) => ACTION_LABELS[v] || v
    },
    {
      title: 'Performed By',
      dataIndex: 'performed_by_name',
      key: 'performed_by_name',
      width: 160
    },
    {
      title: 'Details',
      dataIndex: 'details',
      key: 'details',
      render: (v, record) => {
        if (record.action === 'assigned_to_batch' && v) {
          const d = typeof v === 'string' ? JSON.parse(v) : v;
          return (
            <span>
              {[d.product_batch_number, d.product_name, d.process]
                .filter(Boolean)
                .join(' · ')}
            </span>
          );
        }
        return '—';
      }
    }
  ];

  const bulkTooltip = isMixed
    ? 'Select sieve/screen items of the same status to perform a bulk action'
    : selectedItems.length === 0
    ? 'Select sieve/screen items first'
    : '';

  return (
    <Layout sider={false}>
      <div className={styles.page_wrapper}>
        {/* Header row */}
        <Row
          justify="space-between"
          align="middle"
          className={styles.page_header_row}
        >
          <Col>
            <h2 className={styles.page_title}>Sieve / Screen Allocation</h2>
          </Col>
          <Col>
            <Space>
              <Button
                icon={<ScanOutlined />}
                type={scanFilter ? 'primary' : 'default'}
                onClick={() => setBulkScanModalOpen(true)}
              >
                Scan to Filter
              </Button>
              {scanFilter && (
                <Tag
                  closable
                  onClose={() => {
                    setScanFilter(null);
                    setPage(1);
                    prefetchRef.current = null;
                  }}
                  color="blue"
                  style={{ fontSize: 12 }}
                >
                  {scanFilter.length} item{scanFilter.length !== 1 ? 's' : ''}{' '}
                  filtered
                </Tag>
              )}
              <Button
                icon={<FileTextOutlined />}
                onClick={() => {
                  reportForm.resetFields();
                  setModalType('usage_log');
                }}
              >
                Sieve/Screen Usage Log
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Info Cards */}
        <Row gutter={16} className={styles.cards_row}>
          <Col xs={24} sm={6}>
            <Card
              className={`${styles.info_card} ${styles.card_orange}`}
              onClick={() => handleCardClick('to_be_cleaned')}
            >
              <Statistic title="To be Cleaned" value={counts.to_be_cleaned} />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card
              className={`${styles.info_card} ${styles.card_green}`}
              onClick={() => handleCardClick('cleaned')}
            >
              <Statistic title="Cleaned" value={counts.cleaned} />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card
              className={`${styles.info_card} ${styles.card_blue}`}
              onClick={() => handleCardClick('in_use')}
            >
              <Statistic title="In Use" value={counts.in_use} />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card
              className={`${styles.info_card} ${styles.card_grey}`}
              onClick={() => handleCardClick('inactive')}
            >
              <Statistic title="Inactive" value={counts.inactive || 0} />
            </Card>
          </Col>
        </Row>

        {/* Tab slicer */}
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          className={styles.tab_slicer}
          tabBarExtraContent={
            selectedItems.length > 0
              ? (() => {
                  const tally = {};
                  selectedItems.forEach((i) => {
                    const label = STATUS_CONFIG[i.status]?.label || i.status;
                    tally[label] = (tally[label] || 0) + 1;
                  });
                  const parts = Object.entries(tally).map(
                    ([label, n]) => `${n} ${label}`
                  );
                  return (
                    <span className={styles.selection_badge}>
                      {selectedItems.length} selected — {parts.join(' · ')}
                    </span>
                  );
                })()
              : null
          }
        >
          <TabPane tab="All" key="all" />
          <TabPane
            tab={`To be Cleaned (${counts.to_be_cleaned})`}
            key="to_be_cleaned"
          />
          <TabPane tab={`Cleaned (${counts.cleaned})`} key="cleaned" />
          <TabPane tab={`In Use (${counts.in_use})`} key="in_use" />
          <TabPane tab={`Inactive (${counts.inactive || 0})`} key="inactive" />
        </Tabs>

        {/* Toolbar: batch search + bulk buttons */}
        <Row
          justify="space-between"
          align="middle"
          className={styles.toolbar_row}
        >
          <Col xs={24} md={10} lg={8}>
            <Space align="center">
              <Input.Search
                placeholder="Search by batch number..."
                allowClear
                value={batchSearch}
                onChange={(e) => !e.target.value && setBatchSearch('')}
                onSearch={(v) => {
                  setPage(1);
                  setBatchSearch(v);
                }}
                style={{ width: 220 }}
              />
              {hasActiveFilters && (
                <Button
                  type="text"
                  size="small"
                  icon={<CloseCircleOutlined />}
                  onClick={handleClearFilters}
                  style={{ color: '#ff4d4f' }}
                >
                  Clear Filters
                </Button>
              )}
            </Space>
          </Col>
          <Col>
            <Space align="center">
              <Tooltip title={bulkTooltip}>
                <Button
                  style={
                    canMarkCleaned
                      ? {
                          background: '#f6ffed',
                          borderColor: '#52c41a',
                          color: '#52c41a'
                        }
                      : {}
                  }
                  disabled={!canMarkCleaned || !canAct}
                  onClick={() => setModalType('bulk_clean')}
                >
                  Mark Cleaned
                </Button>
              </Tooltip>
              <Tooltip title={bulkTooltip}>
                <Button
                  type="primary"
                  disabled={!canAssignBatch || !canAct}
                  onClick={() => {
                    bulkAllocForm.resetFields();
                    setBulkAllocStep('form');
                    setBulkAllocFormValues(null);
                    setModalType('bulk_allocate');
                  }}
                >
                  Assign to Batch
                </Button>
              </Tooltip>
              <Tooltip title={bulkTooltip}>
                <Button
                  danger
                  disabled={!canSendForCleaning || !canAct}
                  onClick={() => setModalType('bulk_status')}
                >
                  Send for Cleaning
                </Button>
              </Tooltip>
              <Tooltip
                title={
                  !canInactivate ? 'Select To be Cleaned or Cleaned items' : ''
                }
              >
                <Button
                  icon={<StopOutlined />}
                  disabled={!canInactivate || !canAct}
                  onClick={() => {
                    inactivateForm.resetFields();
                    setModalType('bulk_inactivate');
                  }}
                >
                  Inactivate
                </Button>
              </Tooltip>
            </Space>
          </Col>
        </Row>

        {/* Main table */}
        <div className={submitting ? styles.table_locked : undefined}>
          <CustomTable
            columns={columns}
            data={seededList || list}
            rowKey={(r) => r.id}
            rowSelection={rowSelection}
            loading={loading && !seededList}
            scroll={{ x: 'max-content', y: 600 }}
            rowClassName={(record) => {
              if (highlightedIds.has(record.sieve_label_number))
                return styles.row_highlight;
              if (!record.is_active) return styles.inactive_row;
              return '';
            }}
            locale={{ emptyText: <Empty description={emptyText} /> }}
            pagination={{
              total: listPagination?.total_records || 0,
              pageSize: perPage,
              current: page,
              onChange: handlePageChange
            }}
            onChange={(_, filters) => setFilteredInfo(filters)}
            showPaginationMeta
          />
        </div>

        {/* ── MODALS ── */}

        {/* Bulk Clean — QR scan verification */}
        <SieveScanModal
          scannerId="sieve-scan-clean"
          visible={modalType === 'bulk_clean'}
          title={`Mark ${selectedIds.length} Sieve/Screen Item${
            selectedIds.length > 1 ? 's' : ''
          } as Cleaned`}
          targetLabels={selectedItems.map((i) => i.sieve_label_number)}
          confirmButtonText="Mark Cleaned"
          loading={submitting}
          onCancel={closeModal}
          onConfirm={handleBulkCleanWithScan}
        />

        {/* Bulk Allocate form */}
        <CustomModal
          title={`Assign ${selectedIds.length} Sieve/Screen Item${
            selectedIds.length > 1 ? 's' : ''
          } to Batch`}
          visible={modalType === 'bulk_allocate' && bulkAllocStep === 'form'}
          onCancel={closeModal}
          customFooter={false}
          footer={null}
          width={520}
        >
          <p style={{ marginBottom: 8, color: '#595959' }}>
            These batch details will be applied to all{' '}
            <strong>{selectedIds.length}</strong> selected sieve/screen item
            {selectedIds.length > 1 ? 's' : ''}.
          </p>
          <SieveLabelList
            labels={selectedItems.map((i) => i.sieve_label_number)}
          />
          <Form
            form={bulkAllocForm}
            layout="vertical"
            onFinish={handleBulkAllocate}
            style={{ marginTop: 16 }}
          >
            <Form.Item
              label="Product Batch Number"
              name="product_batch_number"
              rules={[{ required: true, message: 'Required' }]}
            >
              <Input placeholder="e.g. BATCH-2026-001" />
            </Form.Item>
            <Form.Item
              label="Product Name"
              name="product_name"
              rules={[{ required: true, message: 'Required' }]}
            >
              <Input placeholder="e.g. Paracetamol 500mg" />
            </Form.Item>
            <Form.Item
              label="Process"
              name="process"
              rules={[{ required: true, message: 'Required' }]}
            >
              <Select
                placeholder="Select Process"
                showSearch
                optionFilterProp="children"
                allowClear
              >
                {processOptions.map((item) => (
                  <Select.Option key={item.id} value={item.process_label_name}>
                    {item.process_label_name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label="Next Stage"
              name="nextstage"
              rules={[{ required: true, message: 'Required' }]}
            >
              <Select
                placeholder="Select Next Stage"
                showSearch
                optionFilterProp="children"
                allowClear
              >
                {nextStageOptions.map((item) => (
                  <Select.Option key={item.id} value={item.prodlabel_nextstage}>
                    {item.prodlabel_nextstage}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <div style={{ textAlign: 'right' }}>
              <Button
                onClick={() => {
                  bulkAllocForm.resetFields();
                  setBulkAllocStep('form');
                  setBulkAllocFormValues(null);
                  closeModal();
                }}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                disabled={submitting}
                style={{ marginLeft: 8 }}
              >
                Next: Scan Items →
              </Button>
            </div>
          </Form>
        </CustomModal>

        {/* Bulk Allocate — step 2: QR scan verification */}
        <SieveScanModal
          scannerId="sieve-scan-alloc"
          visible={modalType === 'bulk_allocate' && bulkAllocStep === 'scan'}
          title="Scan Sieve/Screen Items — Assign to Batch"
          targetLabels={selectedItems.map((i) => i.sieve_label_number)}
          confirmButtonText="Assign to Batch"
          loading={submitting}
          onCancel={() => {
            setBulkAllocStep('form');
            bulkAllocForm.resetFields();
            setBulkAllocFormValues(null);
          }}
          onConfirm={handleBulkAllocateWithScan}
        />

        {/* Bulk Send for Cleaning confirmation */}
        <CustomModal
          title={`Send ${selectedIds.length} Sieve/Screen Item${
            selectedIds.length > 1 ? 's' : ''
          } for Cleaning`}
          visible={modalType === 'bulk_status'}
          onCancel={closeModal}
          okText="Confirm"
          cancelText="Cancel"
          okButtonProps={{
            loading: submitting,
            disabled: submitting,
            danger: true,
            onClick: handleBulkStatusChange
          }}
          cancelButtonProps={{ disabled: submitting, onClick: closeModal }}
        >
          <p>
            This will close the current batch cycle for{' '}
            <strong>{selectedIds.length}</strong> sieve/screen item
            {selectedIds.length > 1 ? 's' : ''} and mark them as ready for
            cleaning. Are you sure?
          </p>
          <SieveLabelList
            labels={selectedItems.map((i) => i.sieve_label_number)}
          />
        </CustomModal>

        {/* Per-item Activity Log modal */}
        <CustomModal
          title={`${logSieveLabel} — Activity Log`}
          visible={modalType === 'sieve_log'}
          onCancel={closeModal}
          customFooter={false}
          footer={null}
          width={800}
        >
          {sieveLogsLoading ? (
            <div style={{ textAlign: 'center', padding: 32 }}>
              <Spin tip="Loading activity log..." />
            </div>
          ) : (
            <div className={styles.log_table}>
              <Table
                columns={logColumns}
                dataSource={sieveLogs}
                rowKey="id"
                pagination={false}
                size="small"
                scroll={{ x: 'max-content' }}
                locale={{
                  emptyText: (
                    <Empty description="No activity recorded for this item yet." />
                  )
                }}
              />
            </div>
          )}
          <div style={{ textAlign: 'right', marginTop: 16 }}>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadSievePdf}
              loading={reportGenerating}
              style={{ marginRight: 8 }}
            >
              Download PDF
            </Button>
            <Button onClick={closeModal}>Close</Button>
          </div>
        </CustomModal>

        {/* Sieve/Screen Usage Log Report modal */}
        <CustomModal
          title="Generate Sieve/Screen Usage Log"
          visible={modalType === 'usage_log'}
          onCancel={closeModal}
          customFooter={false}
          footer={null}
          width={440}
        >
          <Form
            form={reportForm}
            layout="vertical"
            onFinish={handleGenerateReport}
          >
            <Form.Item
              label="From Date"
              name="from_date"
              rules={[{ required: true, message: 'From date is required' }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                disabledDate={(d) => d && d.isAfter(moment(), 'day')}
                placeholder="Select from date"
              />
            </Form.Item>
            <Form.Item
              label="To Date"
              name="to_date"
              rules={[
                { required: true, message: 'To date is required' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || !getFieldValue('from_date'))
                      return Promise.resolve();
                    if (value.isBefore(getFieldValue('from_date'), 'day')) {
                      return Promise.reject(
                        new Error('To date cannot be before from date')
                      );
                    }
                    return Promise.resolve();
                  }
                })
              ]}
            >
              <DatePicker
                style={{ width: '100%' }}
                disabledDate={(d) => d && d.isAfter(moment(), 'day')}
                placeholder="Select to date"
              />
            </Form.Item>
            <div style={{ textAlign: 'right' }}>
              <Button onClick={closeModal}>Cancel</Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={reportGenerating}
                disabled={reportGenerating}
                style={{ marginLeft: 8 }}
              >
                Generate Report
              </Button>
            </div>
          </Form>
        </CustomModal>

        {/* Bulk Inactivate modal */}
        <CustomModal
          title={`Inactivate ${selectedIds.length} Sieve/Screen Item${
            selectedIds.length > 1 ? 's' : ''
          }`}
          visible={modalType === 'bulk_inactivate'}
          onCancel={closeModal}
          customFooter={false}
          footer={null}
          width={480}
        >
          <Alert
            type="warning"
            showIcon
            message="Inactivated items cannot be used for any operations."
            style={{ marginBottom: 8 }}
          />
          <SieveLabelList
            labels={selectedItems.map((i) => i.sieve_label_number)}
          />
          <Form
            form={inactivateForm}
            style={{ marginTop: 16 }}
            layout="vertical"
            onFinish={handleBulkInactivate}
          >
            <Form.Item
              label="Reason for Inactivation"
              name="reason"
              rules={[
                { required: true, message: 'Reason is required.' },
                { min: 10, message: 'Please provide at least 10 characters.' }
              ]}
            >
              <Input.TextArea
                rows={3}
                maxLength={500}
                showCount
                placeholder="e.g. Item physically damaged and failed inspection"
              />
            </Form.Item>
            <div style={{ textAlign: 'right' }}>
              <Button onClick={closeModal} disabled={submitting}>
                Cancel
              </Button>
              <Button
                danger
                htmlType="submit"
                loading={submitting}
                disabled={submitting}
                style={{ marginLeft: 8 }}
              >
                Confirm Inactivation
              </Button>
            </div>
          </Form>
        </CustomModal>

        {/* Reactivate modal */}
        <CustomModal
          title={`Reactivate ${reactivateSieveLabel}`}
          visible={modalType === 'reactivate'}
          onCancel={() => {
            setModalType(null);
            reactivateForm.resetFields();
          }}
          customFooter={false}
          footer={null}
          width={440}
        >
          <SieveLabelList
            labels={reactivateSieveLabel ? [reactivateSieveLabel] : []}
          />
          <Form
            form={reactivateForm}
            layout="vertical"
            onFinish={handleReactivate}
            style={{ marginTop: 16 }}
          >
            <Form.Item
              label="Reactivation Note"
              name="note"
              rules={[{ required: true, message: 'Note is required.' }]}
            >
              <Input.TextArea
                rows={3}
                maxLength={500}
                showCount
                placeholder="e.g. Item repaired and cleared after inspection"
              />
            </Form.Item>
            <div style={{ textAlign: 'right' }}>
              <Button
                onClick={() => {
                  setModalType(null);
                  reactivateForm.resetFields();
                }}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                disabled={submitting}
                style={{ marginLeft: 8 }}
                icon={<CheckCircleOutlined />}
              >
                Reactivate
              </Button>
            </div>
          </Form>
        </CustomModal>

        {/* Bulk Scan to Filter */}
        <BulkSieveScanFilterModal
          visible={bulkScanModalOpen}
          onCancel={() => setBulkScanModalOpen(false)}
          onApply={(labels) => {
            setScanFilter(labels);
            setPage(1);
            prefetchRef.current = null;
            setBulkScanModalOpen(false);
          }}
        />
      </div>
    </Layout>
  );
};

export default SieveAllocation;
